import React from "react";

export type PhaseStatus = "incomplete" | "partial" | "complete";
export type Role = "user" | "ai";

export type ConversationMsg = {
    role: Role;
    text?: string;
    html?: string;
    ts?: string;
    files?: { type: string; src: string }[]; // aggiunta per supportare allegati
};

export type Attachment = {
    type: string;
    url: string;
    title?: string;
};


export type Phase = {
    index: number;
    name: string;
    status: PhaseStatus;
    brief: string;
    // se vuoi tenere separate le keywords tecniche, aggiungi anche questa:
    technicalKeywords?: string[];

    keywords: string[];
    palette: string[];
    attachments: Attachment[];
    conversation: ConversationMsg[];
    updated_at?: string; // per tenere traccia dell'ultima modifica di ogni fase
};

export type Project = {
    project_id: string;
    title: string;
    created_at: string;
    updated_at: string;
    current_phase: number;
    phases: Phase[];
};

const nowIso = () => new Date().toISOString();

function buildDefaultPhases(): Phase[] {
    return [
        { index: 1, name: "Brief Creativo", status: "incomplete", brief: "", keywords: [], palette: [], attachments: [], conversation: [] },
        { index: 2, name: "Moodboard & Brief tecnico", status: "incomplete", brief: "", technicalKeywords: [], keywords: [], palette: [], attachments: [], conversation: [] },
        { index: 3, name: "Schizzo e bozzetti", status: "incomplete", brief: "", technicalKeywords: [], keywords: [], palette: [], attachments: [], conversation: [] },
        { index: 4, name: "Elaborazione 3D", status: "incomplete", brief: "", keywords: [], palette: [], attachments: [], conversation: [] },
    ];
}

function ensureProjectHasDefaultPhases(remote: Project): Project {
    const defaultPhases = buildDefaultPhases();
    const remotePhases = Array.isArray(remote?.phases)
        ? remote.phases.map((phase) => ({
            ...phase,
            status: phase?.status ?? "incomplete",
        }))
        : [];

    const missingPhases = defaultPhases.filter(
        (defaultPhase) => !remotePhases.some((phase) => phase?.index === defaultPhase.index)
    );

    if (missingPhases.length === 0) {
        return {
            ...remote,
            phases: remotePhases,
        };
    }

    return {
        ...remote,
        phases: [...remotePhases, ...missingPhases].sort((a, b) => a.index - b.index),
    };
}

function normalizeProjectForSave(project: Project, phaseIndex: number): Project {
    const normalizedPhases = (project.phases || []).map((phase) => {
        if (phase.index < phaseIndex) return { ...phase, status: "complete" as PhaseStatus };
        if (phase.index === phaseIndex) return { ...phase, status: "partial" as PhaseStatus };
        return { ...phase, status: "incomplete" as PhaseStatus };
    });

    return {
        ...project,
        current_phase: phaseIndex,
        phases: normalizedPhases,
    };
}

export function makeEmptyProject(projectId: string): Project {
    return {
        project_id: projectId,
        title: "Nuovo progetto",
        created_at: nowIso(),
        updated_at: nowIso(),
        current_phase: 1,
        phases: buildDefaultPhases(),
    };
}

async function apiGet(projectId: string): Promise<Project | null> {

//load base url da config
    const cfg = (window as any).__APP_CONFIG__;
    const PROJECTS_API_BASE = cfg.PROJECTS_API_BASE.replace(/\/$/, "");
    console.log("Using PROJECTS_API_BASE:", PROJECTS_API_BASE);

    const res = await fetch(`${PROJECTS_API_BASE}/${encodeURIComponent(projectId)}`, {
        method: "GET",
        headers: { Accept: "application/json" },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`GET project failed (${res.status})`);
    return await res.json();
}

async function apiPost(projectId: string, payload: Project): Promise<Project> {

//load base url da config
    const cfg = (window as any).__APP_CONFIG__;
    const PROJECTS_API_BASE = cfg.PROJECTS_API_BASE.replace(/\/$/, "");

    console.log("Using PROJECTS_API_BASE:", PROJECTS_API_BASE);

    console.log("apiPost", projectId, payload);
    const res = await fetch(`${PROJECTS_API_BASE}/${encodeURIComponent(projectId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`POST project failed (${res.status})`);
    return await res.json(); // il server ti ritorna il progetto con updated_at coerente
}

type Ctx = {
    project: Project;
    isLoading: boolean;
    loadError: string | null;

    appendIncomingMessages: (phaseIndex: number, incoming: any[]) => void;
    applyAiResponseAndSave: (phaseIndex: number, r: any) => Promise<void>;
    addAttachmentAndSave: (phaseIndex: number, url: string, type: string, title: string) => Promise<void>;

    // ✅ (facoltativo) salvataggio manuale del project corrente
    saveNow: (payload?: Project) => Promise<void>;

};

const ProjectContext = React.createContext<Ctx | null>(null);

export function ProjectProvider({ projectId, children }: { projectId: string; children: React.ReactNode }) {
    const [project, setProject] = React.useState<Project>(() => makeEmptyProject(projectId));
    const [isLoading, setIsLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<string | null>(null);

    // usati per evitare autosave durante load / e per evitare loop
    const hydratedRef = React.useRef(false);


    // --- LOAD iniziale
    React.useEffect(() => {
        let alive = true;

        (async () => {
            try {
                setIsLoading(true);
                setLoadError(null);
                hydratedRef.current = false;

                const remote = await apiGet(projectId);
                if (!alive) return;
                


                if (remote) {
                    const normalizedRemote = ensureProjectHasDefaultPhases(remote);
                    setProject(normalizedRemote);
                } else {
                    const fresh = makeEmptyProject(projectId);
                    const normalizedFresh = normalizeProjectForSave(fresh, fresh.current_phase);
                    setProject(normalizedFresh);
                    // opzionale: salva subito la creazione file
                    await apiPost(projectId, normalizedFresh);
                }

                hydratedRef.current = true;
            } catch (e: any) {
                if (!alive) return;
                setLoadError(e?.message ?? "Errore caricamento progetto");
                setProject(makeEmptyProject(projectId));
                hydratedRef.current = true;
            } finally {
                if (!alive) return;
                setIsLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [projectId]);

    const addAttachmentAndSave = React.useCallback(
        async (phaseIndex: number, url: string, type: string, title: string) => {

            let nextToSave: Project | null = null;

            console.log("addAttachmentAndSave called with url", url, "for phase", phaseIndex);

            setProject(prev => {
                const phases = prev.phases.map(ph => {
                    if (ph.index !== phaseIndex) return ph;

                    //set update_at as current date time. Es:      "updated_at": "2026-02-07T15:24:52.838Z",
                    ph.updated_at = nowIso();

                    // evita duplicati
                    //if (ph.attachments?.includes(url)) return ph;

                    //create new attachment object
                    const newAttachment: Attachment = { type, url, title };


                    return {
                        ...ph,
                        attachments: [...(ph.attachments || []), newAttachment],
                    };
                });

                console.log("Updated phases with new attachment:", phases);

                const next = { ...prev, phases, current_phase: phaseIndex };
                const normalizedNext = normalizeProjectForSave(next, phaseIndex);
                nextToSave = normalizedNext;
                return normalizedNext;
            });

            if (nextToSave) {
                await apiPost(projectId, normalizeProjectForSave(nextToSave, phaseIndex));
            }
        },
        [projectId]
    );


    // --- Mutators
    const appendMessage = React.useCallback((phaseIndex: number, msg: ConversationMsg) => {
        setProject((p) => {
            const phases = p.phases.map((ph) =>
                ph.index === phaseIndex
                    ? { ...ph, conversation: [...ph.conversation, { ...msg, ts: msg.ts ?? nowIso() }] }
                    : ph
            );
            return { ...p, phases, current_phase: phaseIndex };
        });
    }, []);


    const appendIncomingMessages = React.useCallback((phaseIndex: number, incoming: any[]) => {
        setProject((prev) => {
            const add: ConversationMsg[] = (incoming || [])
                .filter(m => m && (m.text || m.html))
                .map(m => ({
                    role: (m.role === "ai" ? "ai" : "user"),
                    text: m.text ? String(m.text) : undefined,
                    html: m.html ? String(m.html) : undefined,
                    ts: nowIso(),
                }));

            const phases = prev.phases.map(ph =>
                ph.index === phaseIndex
                    ? { ...ph, conversation: [...(ph.conversation || []), ...add] }
                    : ph
            );

            return { ...prev, phases, current_phase: phaseIndex };
        });
    }, []);

    const applyAiResponseAndSave = React.useCallback(async (phaseIndex: number, r: any) => {
        let nextToSave: Project | null = null;

        console.log("applyAiResponseAndSave called with response to phase", phaseIndex, r);

        setProject((prev) => {
            const aiText = r?.text ?? r?.message ?? r?.response ?? "";
            const aiHtml = r?.html ? String(r.html) : undefined;

            //se esiste r.files[0]
            //allora prendi il primo elemento di r.files come messaggio da allegare alla conversazioner.files[0].src e r.files[0].type come messaggio allegare alla conversazione
            const aiAttachment = r?.files && Array.isArray(r.files) && r.files.length > 0 ? r.files[0] : null;

            const patch: Partial<Phase> = {
                status: r?.brief_status ?? undefined,
                brief: r?.brief ?? undefined,
                // se in fase 2 usi technicalKeywords come “keywords”
                keywords:
                    phaseIndex === 2 && Array.isArray(r?.technicalKeywords)
                        ? r.technicalKeywords
                        : Array.isArray(r?.keywords)
                            ? r.keywords
                            : undefined,
                palette: Array.isArray(r?.palette) ? r.palette : undefined,
                //se aiAttachment esiste, aggiungi il suo src a attachments
                //attachments: aiAttachment ? [...(prev.phases.find(ph => ph.index === phaseIndex)?.attachments || []), String(aiAttachment.src)] : undefined,

            };

            const phases = prev.phases.map((ph) => {
                if (ph.index !== phaseIndex) return ph;

                const conversation = [...(ph.conversation || [])];

                if (aiText) conversation.push({ role: "ai", text: String(aiText), ts: nowIso() });
                if (aiHtml) conversation.push({ role: "ai", html: aiHtml, ts: nowIso() });
                if (aiAttachment) conversation.push({
                    role: "ai",
                    files: [{ type: String(aiAttachment.type), src: String(aiAttachment.src) }],
                    ts: nowIso(),
                });
                ph.updated_at = nowIso();

                return { ...ph, ...patch, conversation };
            });

            const next: Project = { ...prev, phases, current_phase: phaseIndex };
            const normalizedNext = normalizeProjectForSave(next, phaseIndex);
            nextToSave = normalizedNext;
            return normalizedNext;
        });

        // salva il NEXT certo
        if (nextToSave) {
            await apiPost(projectId, normalizeProjectForSave(nextToSave, phaseIndex));
        }
    }, [projectId]);





    const saveNow = React.useCallback(async () => {
        const phaseIndex = project?.current_phase ?? 1;
        const normalizedProject = normalizeProjectForSave(project, phaseIndex);
        console.log("saveNow called with project", normalizedProject);
        await apiPost(projectId, normalizedProject);
    }, [projectId, project]);



    const value: Ctx = {
        project,
        isLoading,
        loadError,
        appendIncomingMessages,
        applyAiResponseAndSave,
        addAttachmentAndSave,
        saveNow,
    };

    return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject() {
    const ctx = React.useContext(ProjectContext);
    if (!ctx) throw new Error("useProject must be used within ProjectProvider");
    return ctx;
}
